<script setup lang="ts">
import type { ModelTypes } from '~/services-v2/financial-bff/__generated__/zeus'
import type { StatusInstallation } from '~/utils/installation/Installation'
import ContactSupportIcon from '~icons/material-symbols/contact-support-outline'
import LocalShippingIcon from '~icons/material-symbols/local-shipping-outline'

defineProps<{
  statusInstallation: StatusInstallation
  installationData: ModelTypes['Installation']
  hasManual: boolean
  isStatusWaiting: boolean
  openForm: boolean
}>()

defineEmits<{
  (e: 'openTrackAmperaLink'): void
  (e: 'followManuallyOpenForm'): void
  (e: 'openHelp'): void
}>()

const { t } = useI18n()

function parseFormattedText(text?: string) {
  if (!text)
    return ''

  return text
    .replace(/\[strong\]/g, '<strong>')
    .replace(/\[\/strong\]/g, '</strong>')
}
</script>

<template>
  <div class="validation-card-container">
    <h1 v-if="statusInstallation?.pageTitle" class="page-title">
      {{ statusInstallation.pageTitle }}
    </h1>

    <SolDivider v-if="statusInstallation?.pageTitle" thickness="x-small" orientation="horizontal" />

    <div class="flex flex-col gap-2 md:site:flex-row md:site:gap-8 md:site:items-center">
      <figure class="mt-2xs mb-4xs m-auto">
        <img
          v-if="statusInstallation.img"
          :src="`/images/${statusInstallation.img.name}`"
          :alt="statusInstallation.img.alt"
          :title="statusInstallation.img.title"
          class="image"
        >
      </figure>

      <div class="w-full">
        <h2 class="title">
          {{ statusInstallation.title }}
        </h2>

        <p class="subtitle md:site:mt-nano" v-html="parseFormattedText(statusInstallation.subtitle)" />

        <SolLink
          v-if="installationData.has_ampera"
          id="go-to-validation-help"
          size="medium"
          class="m-auto mt-nano w-fit-content md:site:ml-0"
          @click="$emit('openHelp')"
        >
          <template #icon>
            <ContactSupportIcon class="icon-size-small" />
          </template>

          {{ t('installationPage.helpButton') }}
        </SolLink>
      </div>
    </div>

    <SolDivider thickness="x-small" orientation="horizontal" class="mt-2xs mb-4xs" />

    <div class="flex flex-col gap-2 md:site:flex-row justify-end">
      <SolButton
        v-if="hasManual"
        id="manualFollowButton"
        variant="primary"
        size="large"
        :disabled="(
          installationData.has_ampera
          && installationData.has_minimal_projects
          && !isStatusWaiting
        ) || openForm"
        class="w-full md:site:w-auto md:site:order-2"
        @click="$emit('followManuallyOpenForm')"
      >
        {{ t('installationPage.manualButton') }}
      </SolButton>

      <SolButton
        v-if="installationData.has_ampera"
        id="trackAmperaButton"
        variant="tertiary"
        size="large"
        class="w-full md:site:w-auto md:site:order-1"
        @click="$emit('openTrackAmperaLink')"
      >
        <template #icon:left>
          <LocalShippingIcon class="icon-size-medium" />
        </template>

        {{ t('installationPage.trackAmpera') }}
      </SolButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.validation-card-container {
  @apply flex flex-col justify-center gap-2;
  //@apply px-4xs py-2xs rounded-sm;
  @apply bg-neutral-high-pure;
  @apply w-full;
  @apply font-base leading-xs;

  .image {
    width: 160px;
    height: 135px;
    aspect-ratio: 32/27;
  }

  > .page-title {
    @apply font-highlight;
    @apply text-xs md:site:text-sm text-neutral-low-dark;
  }

  .title {
    @apply font-bold;
    @apply text-xs md:site:text-2xs text-neutral-low-dark;
    @apply text-center md:site:text-left;
  }

  .subtitle {
    @apply text-3xs text-neutral-low-light;
    @apply text-center md:site:text-left;
  }
}
</style>
